import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation";

function RecoveryView() {
  const { recoveryId } = useParams();
  const navigate = useNavigate();

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [recovery, setRecovery] = useState(null);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState('ur');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const recoveryContentRef = useRef(null);

  useEffect(() => {
    fetchRecoveryData();
  }, [recoveryId]);

  const fetchRecoveryData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const response = await api.get(`/recovery/company/${recoveryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRecovery(response.data.recovery);
      const companyData = response.data.company;
      setCompany(companyData);

      const dashboardType = companyData?.dashboardType || companyData?.dashboard_type || 'main';
      const isPart2 = dashboardType === 'part2' || dashboardType === 'dashboard-part2' || dashboardType === 'part-2' || dashboardType === 'ur' || dashboardType === 'urdu';

      setLanguage(isPart2 ? 'ur' : (companyData?.language || 'ur'));
    } catch (error) {
      console.error("Fetch recovery error:", error);
      toast.error("ریکوری رسید لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!recoveryContentRef.current) return;

    try {
      setIsGeneratingPDF(true);
      const canvas = await html2canvas(recoveryContentRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Recovery-Receipt-${recovery?.serialNumber || recoveryId}.pdf`);
      toast.success("PDF ڈاؤن لوڈ ہو گیا!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("PDF بنانے میں ناکام");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
          <p className={`mt-4 text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>لوڈ ہو رہا ہے...</p>
        </div>
      </div>
    );
  }

  if (!recovery) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center">
        <div className="text-center">
          <p className={`text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>ریکوری رسید نہیں ملی</p>
          <button
            onClick={() => navigate('/company/dashboard')}
            className="mt-4 px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105"
          >
            {language === 'ur' ? 'ڈیش بورڈ پر واپس جائیں' : 'Back to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>ریکوری رسید #{recovery.serialNumber || recoveryId}</title>
      </Helmet>
      <div className={`min-h-screen bg-brand-background py-8 px-4 ${language === 'ur' ? 'font-urdu' : ''}`} dir={language === 'ur' ? 'rtl' : 'ltr'}>
        <div className="max-w-4xl mx-auto">
          {/* Action Buttons */}
          <div className="mb-6 flex gap-4 justify-end print:hidden">
            <button
              onClick={() => navigate('/company/dashboard')}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all transform hover:scale-105"
            >
              {language === 'ur' ? 'واپس' : 'Back'}
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-brand-header hover:bg-emerald-800 text-white rounded-lg transition-all transform hover:scale-105"
            >
              {language === 'ur' ? 'پرنٹ' : 'Print'}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all transform hover:scale-105 disabled:opacity-50"
            >
              {isGeneratingPDF
                ? (language === 'ur' ? 'بنا رہے ہیں...' : 'Generating...')
                : (language === 'ur' ? 'PDF ڈاؤن لوڈ' : 'Download PDF')
              }
            </button>
          </div>

          {/* Receipt Content */}
          <div
            ref={recoveryContentRef}
            className="bg-white shadow-lg rounded-lg p-8 print:shadow-none print:p-6 border border-brand-card"
            style={{ fontFamily: language === 'ur' ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : "system-ui, sans-serif" }}
          >
            {/* Header */}
            <div className="border-b-2 border-brand-header pb-4 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  {company?.logo && (
                    <img
                      src={company.logo}
                      alt={company.shopName || "Company Logo"}
                      className="h-16 w-auto mb-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <h1 className={`text-3xl font-bold text-brand-header ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {company?.shopName || company?.username || 'Company Name'}
                  </h1>
                  {company?.address && (
                    <p className={`text-sm text-gray-600 mt-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {company.address}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <h2 className={`text-2xl font-bold text-brand-header mb-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'وصولی رسید' : 'Recovery Receipt'}
                  </h2>
                  <p className={`text-sm text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'رسید نمبر:' : 'Receipt #'}: <span className="font-semibold text-brand-header">{recovery.serialNumber || recoveryId.slice(-6)}</span>
                  </p>
                  <p className={`text-sm text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تاریخ:' : 'Date'}: {new Date(recovery.date).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="mb-6">
              <div className="border border-brand-card rounded-lg p-4 bg-brand-background">
                <h3 className={`text-lg font-semibold text-brand-header mb-3 ${language === 'ur' ? 'font-urdu' : ''}`}>
                  {language === 'ur' ? 'گاہک کی معلومات' : 'Customer Information'}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm font-medium text-gray-600 mb-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {language === 'ur' ? 'نام:' : 'Name:'}
                    </p>
                    <p className={`text-base font-semibold text-gray-900 ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {recovery.customerNameUrdu || recovery.customerName || '-'}
                    </p>
                  </div>
                  {recovery.customerPhone && (
                    <div>
                      <p className={`text-sm font-medium text-gray-600 mb-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'فون نمبر:' : 'Phone Number:'}
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {recovery.customerPhone}
                      </p>
                    </div>
                  )}
                  {recovery.customerVehicleNumber && (
                    <div>
                      <p className={`text-sm font-medium text-gray-600 mb-1 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'گاڑی نمبر:' : 'Vehicle Number:'}
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        {recovery.customerVehicleNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recovery Details */}
            <div className="mb-6">
              <h3 className={`text-lg font-semibold text-brand-header mb-3 ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur' ? 'وصولی کی تفصیل' : 'Recovery Details'}
              </h3>
              <div className="border border-brand-card rounded-lg p-6 bg-white">
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-3">
                    <span className={`text-base font-medium text-gray-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                      {language === 'ur' ? 'وصولی کی رقم:' : 'Recovery Amount:'}
                    </span>
                    <span className="text-2xl font-bold text-emerald-600">
                      Rs. {(recovery.recoveryAmount || 0).toLocaleString()}
                    </span>
                  </div>
                  {recovery.paymentMethod && (
                    <div className="flex justify-between items-center">
                      <span className={`text-sm font-medium text-gray-600 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'ادائیگی کا طریقہ:' : 'Payment Method:'}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {recovery.paymentMethod}
                      </span>
                    </div>
                  )}
                  {(recovery.description || recovery.descriptionUrdu) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className={`text-sm font-medium text-gray-600 mb-2 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {language === 'ur' ? 'تفصیل:' : 'Description:'}
                      </p>
                      <p className={`text-sm text-gray-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {recovery.descriptionUrdu || recovery.description || '-'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Box */}
            <div className="border-t-2 border-brand-header pt-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className={`text-lg font-bold text-brand-header ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'کل وصولی:' : 'Total Recovery:'}
                  </span>
                  <span className="text-2xl font-bold text-emerald-700">
                    Rs. {(recovery.recoveryAmount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t border-gray-300 text-center">
              <p className={`text-xs text-gray-500 ${language === 'ur' ? 'font-urdu' : ''}`}>
                {language === 'ur'
                  ? 'یہ رسید کمپیوٹر سے تیار کی گئی ہے اور دستخط کی ضرورت نہیں'
                  : 'This receipt is computer generated and does not require a signature'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:shadow-none,
          .print\\:shadow-none * {
            visibility: visible;
          }
          .print\\:shadow-none {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default RecoveryView;

